/**
 * Utility function to automatically scroll to show dropdown options when they are not visible
 * @param dropdownRef - React ref to the dropdown container element
 * @param dataAttribute - The data attribute to identify the dropdown options element (default: "data-dropdown-options")
 */
export const scrollToShowDropdown = (
  dropdownRef: React.RefObject<HTMLDivElement>,
  dataAttribute = "data-dropdown-options"
): void => {
  if (!dropdownRef.current) return;

  const dropdownElement = dropdownRef.current;
  const dropdownOptions = dropdownElement.querySelector(
    `[${dataAttribute}]`
  ) as HTMLElement;

  if (!dropdownOptions) return;

  // Find the scrollable container (modal or main page)
  const findScrollableContainer = (element: HTMLElement): HTMLElement => {
    let parent = element.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflow = style.overflow + style.overflowY;
      if (overflow.includes("auto") || overflow.includes("scroll")) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return document.body;
  };

  const scrollableContainer = findScrollableContainer(dropdownElement);
  const isModal = scrollableContainer !== document.body;

  // Get the bounding rectangles
  const optionsRect = dropdownOptions.getBoundingClientRect();

  // Check if the dropdown options are below the visible area
  const isOptionsBelowViewport =
    optionsRect.bottom >
    (isModal
      ? scrollableContainer.getBoundingClientRect().bottom
      : window.innerHeight);

  if (isOptionsBelowViewport) {
    // Calculate how much to scroll to show the options
    const scrollAmount =
      optionsRect.bottom -
      (isModal
        ? scrollableContainer.getBoundingClientRect().bottom
        : window.innerHeight) +
      20; // 20px padding

    // Smooth scroll in the appropriate container
    if (isModal) {
      scrollableContainer.scrollBy({
        top: scrollAmount,
        behavior: "smooth",
      });
    } else {
      window.scrollBy({
        top: scrollAmount,
        behavior: "smooth",
      });
    }
  }
};
